import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTutorial } from '../../context/TutorialContext';
import SuggestionDropdown from '../shared/SuggestionDropdown';
import DateDropdown from '../shared/DateDropdown';
import { filterDateCommands } from '../../utils/dateCommandUtils';
import RichTextEditor from './RichTextEditor';
import { toast } from '../../utils/customToast';
import useMessageSettings from '../../hooks/useMessageSettings';
import { usePlanPermissions, getCurrentPlan, UpgradeMessage } from '../../utils/planPermissions';
import { FiMoreVertical } from 'react-icons/fi';

const MESSAGE_TYPES = [
    'Rappel mensuel',
    'Rappel hebdomadaire',
    'Rappel quotidien',
    'Notification',
    'Information',
    'Alerte',
    'Mise à jour'
];

const MESSAGE_TYPE_COLORS = {
    'Rappel mensuel': '#3b82f6',
    'Rappel hebdomadaire': '#8b5cf6',
    'Rappel quotidien': '#06b6d4',
    'Notification': '#f59e0b',
    'Information': '#10b981',
    'Alerte': '#ef4444',
    'Mise à jour': '#6366f1'
};

const ROLES = ['CIO Office', 'PMO', 'Système'];
const apiBase = process.env.REACT_APP_API_URL || '/api';

const MessageEditor = ({ message, onSave, onCancel }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [type, setType] = useState(MESSAGE_TYPES[0]);
    const [role, setRole] = useState(ROLES[0]);
    const [priority, setPriority] = useState('moyenne');
    const { getAuthHeaders, activeTenantId, user } = useAuth();
    
    const { notifyAction: tutorialNotify } = useTutorial();

    // Signaler au tutoriel que l'éditeur est ouvert (step create-message → editor-opened)
    useEffect(() => {
        tutorialNotify('editor-opened');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Permissions basées sur le plan
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canMentionUsers, canMentionTodos, canMentionMessages, canUseTemplates, canUseDateCommands } = usePlanPermissions(currentPlan);
    const { messageTypes, messageRoles } = useMessageSettings();
    const [allMessages, setAllMessages] = useState([]);
    const [allTodos, setAllTodos] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
    const templateMenuRef = useRef(null);

    // Normalize types to array of objects { name, color }
    const normalizedTypes = useMemo(() => {
        if (messageTypes && messageTypes.length) {
            return messageTypes.map(t =>
                typeof t === 'string'
                    ? { name: t, color: MESSAGE_TYPE_COLORS[t] || '#000000' }
                    : { name: t.name, color: t.color || MESSAGE_TYPE_COLORS[t.name] || '#000000' }
            );
        }

        return MESSAGE_TYPES.map(n => ({ name: n, color: MESSAGE_TYPE_COLORS[n] || '#000000' }));
    }, [messageTypes]);

    const roles = (messageRoles && messageRoles.length) ? messageRoles : ROLES;

    const [suggestOpen, setSuggestOpen] = useState(false);
    const [suggestItems, setSuggestItems] = useState([]);
    const [suggestAnchor, setSuggestAnchor] = useState(null);
    const [suggestActive, setSuggestActive] = useState(0);

    // Date dynamic commands
    const [dateSuggestOpen, setDateSuggestOpen] = useState(false);
    const [dateSuggestItems, setDateSuggestItems] = useState([]);
    const [dateSuggestAnchor, setDateSuggestAnchor] = useState(null);
    const [dateSuggestActive, setDateSuggestActive] = useState(0);

    const editorRef = useRef(null);

    useEffect(() => {
        if (message) {
            setTitle(message.title || '');
            setContent(message.content || '');
            setType(message.type);
            setRole(message.role);
            setPriority(message.priority || 'moyenne');
        } else {
            setTitle('');
            setContent('');
            setType(normalizedTypes[0]?.name);
            setRole(roles[0]);
            setPriority('moyenne');
        }
    }, [message, normalizedTypes, roles]);

    useEffect(() => {
        (async () => {
            try {
                // Only fetch templates if plan allows it
                const fetches = [
                    fetch(`${apiBase}/messages`, { headers: getAuthHeaders() }),
                    fetch(`${apiBase}/todos`, { headers: getAuthHeaders() }),
                    fetch(`${apiBase}/tenants/${activeTenantId}/users`, { headers: getAuthHeaders() })
                ];

                if (canUseTemplates) {
                    fetches.push(fetch(`${apiBase}/message-templates`, { headers: getAuthHeaders() }));
                }

                const results = await Promise.all(fetches);
                const [mRes, tRes, uRes, templRes] = results;

                if (mRes.ok) setAllMessages(await mRes.json());
                if (tRes.ok) setAllTodos(await tRes.json());
                if (uRes.ok) setAllUsers(await uRes.json());
                if (templRes && templRes.ok) setTemplates(await templRes.json());
            } catch (e) {
                console.error('Error fetching data:', e);
            }
        })();
    }, [getAuthHeaders, activeTenantId, canUseTemplates]);

    const [loading, setLoading] = useState(false);
    const createLock = useRef(false);
    
    const handleTemplateSelect = (template) => {
        if (template) {
            setTitle(template.title);
            setContent(template.content);
            setType(template.type);
            setRole(template.role);
            setPriority(template.priority || 'moyenne');
            setTemplateMenuOpen(false);
            
            toast.info('Modèle chargé - vous pouvez le modifier', {
                position: "top-right",
                autoClose: 3000,
            });
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading || createLock.current) return;

        // Validation basique
        if (!title.trim()) {
            toast.error('Veuillez renseigner le titre', {
                position: "top-right",
                autoClose: 5000,
            });
            return;
        }

        // Vérifier si l'éditeur est vide
        if (editorRef.current?.isEmpty()) {
            toast.error('Veuillez renseigner le contenu', {
                position: "top-right",
                autoClose: 5000,
            });
            editorRef.current?.focus();
            return;
        }

        setLoading(true);
        createLock.current = true;
        try {
            const newMessage = { title, content, type, role, priority };
            await onSave(newMessage);
        } finally {
            setLoading(false);
            createLock.current = false;
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
            return;
        }
        setTitle('');
        setContent('');
        setType(normalizedTypes[0]?.name);
        setRole(roles[0]);
        setPriority('moyenne');
        setSuggestOpen(false);
    };

    const handleMentionTrigger = (data) => {
        if (!data.isActive) {
            setSuggestOpen(false);
            return;
        }

        const query = (data.query || '').toLowerCase();
        const currentId = message?._id;

        const msgs = canMentionMessages ? (allMessages || [])
            .filter(m => {
                if (currentId && m._id === currentId) return false;
                return m.title?.toLowerCase().includes(query) ||
                    m.content?.toLowerCase().includes(query);
            })
            .slice(0, 20)
            .map(m => ({ ...m, kind: 'msg' })) : [];

        const todos = canMentionTodos ? (allTodos || [])
            .filter(t => {
                if (currentId && t._id === currentId) return false;
                return t.text?.toLowerCase().includes(query);
            })
            .slice(0, 20)
            .map(t => ({ ...t, kind: 'todo' })) : [];

        // Filtrer les utilisateurs selon le plan
        const users = canMentionUsers ? (allUsers || [])
            .filter(u => {
                return u.name?.toLowerCase().includes(query) ||
                    u.email?.toLowerCase().includes(query);
            })
            .slice(0, 20)
            .map(u => ({ ...u, kind: 'user' })) : [];

        const items = [...msgs, ...todos, ...users];
        setSuggestItems(items);
        setSuggestActive(0);
        setSuggestOpen(items.length > 0);
        setSuggestAnchor(data.rect);
    };

    const handleMentionClick = () => {
        if (editorRef.current) {
            const editor = editorRef.current;
            
            // Insert @ character
            if (editor.insertContent) {
                editor.insertContent('@');
            }
            
            // Trigger mention dropdown manually
            handleMentionTrigger({ isActive: true, query: '', rect: null });
        }
    };

    const applySuggestion = (item) => {
        let mention = '';
        if (item.kind === 'msg') {
            mention = `@msg:${item._id}`;
        } else if (item.kind === 'todo') {
            mention = `@todo:${item._id}`;
        } else if (item.kind === 'user') {
            mention = `@user:${item._id}[${item.name}]`;
        }

        if (editorRef.current?.insertMention) {
            editorRef.current.insertMention(mention);
        }

        setSuggestOpen(false);

        setTimeout(() => {
            editorRef.current?.focus();
        }, 0);
    };

    const handleDateTrigger = (data) => {
        if (!data.isActive) {
            setDateSuggestOpen(false);
            return;
        }
        const items = filterDateCommands(data.query || '');
        setDateSuggestItems(items);
        setDateSuggestActive(0);
        setDateSuggestOpen(items.length > 0);
        setDateSuggestAnchor(data.rect);
    };

    const applyDateSuggestion = (item) => {
        if (editorRef.current?.insertDateCommand) {
            // Insère une balise persistante {{date:Commande}} résolue dynamiquement à l'affichage
            editorRef.current.insertDateCommand(`{{date:${item.command}}}`);
        }
        setDateSuggestOpen(false);
        setTimeout(() => editorRef.current?.focus(), 0);
    };

    // Navigation clavier avec preventDefault sur Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!suggestOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestActive(prev => Math.min(prev + 1, suggestItems.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestActive(prev => Math.max(prev - 1, 0));
                    break;
                case 'Enter':
                    if (suggestItems[suggestActive]) {
                        e.preventDefault();
                        e.stopPropagation();
                        applySuggestion(suggestItems[suggestActive]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestOpen(false);
                    break;
                default:
                    break;
            }
        };

        // Utiliser capture: true pour intercepter l'événement avant qu'il n'atteigne l'éditeur
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [suggestOpen, suggestItems, suggestActive]);

    // Close template menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (templateMenuRef.current && !templateMenuRef.current.contains(event.target)) {
                setTemplateMenuOpen(false);
            }
        };

        if (templateMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [templateMenuOpen]);

    return (
        <div className="relative">
            <form onSubmit={handleSubmit} className="p-4 border dark:border-gray-700 rounded shadow-md bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-black dark:text-white">
                        {(message && message._id) ? 'Modifier' : 'Nouveau message'}
                    </h2>
                    
                    {/* Template icon menu - only show when creating a new message and plan is Team+ */}
                    {!(message && message._id) && templates.length > 0 && canUseTemplates && (
                        <div className="relative" ref={templateMenuRef}>
                            <button
                                type="button"
                                onClick={() => setTemplateMenuOpen(!templateMenuOpen)}
                                className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition"
                                title="Utiliser un modèle"
                            >
                                <FiMoreVertical size={20} />
                            </button>
                            
                            {templateMenuOpen && (
                                <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                                    <div className="p-2 border-b dark:border-gray-700">
                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                            Modèles de messages pré-définis
                                        </p>
                                    </div>
                                    {templates.map(template => (
                                        <button
                                            key={template._id}
                                            type="button"
                                            onClick={() => handleTemplateSelect(template)}
                                            className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 border-b dark:border-gray-700 last:border-b-0 transition"
                                        >
                                            <div className="font-medium text-sm text-gray-900 dark:text-white">
                                                {template.name}
                                            </div>
                                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                                {template.title}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Titre *
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-black dark:focus:border-white focus:ring focus:ring-black dark:focus:ring-white px-2 py-1"
                        required
                    />
                </div>

                <div className="mb-4" data-tutorial="message-content">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Contenu *
                    </label>
                    <RichTextEditor
                        ref={editorRef}
                        content={content}
                        onChange={setContent}
                        placeholder={canMentionUsers || canMentionTodos || canMentionMessages
                            ? (canUseDateCommands ? "Tapez votre message... @ pour mentionner, // pour une date" : "Tapez votre message... Utilisez @ pour mentionner")
                            : (canUseDateCommands ? "Tapez votre message... // pour insérer une date dynamique" : "Tapez votre message...")}
                        minHeight="150px"
                        onMentionTrigger={handleMentionTrigger}
                        onMentionClick={handleMentionClick}
                        hasMentionSupport={canMentionUsers || canMentionTodos || canMentionMessages}
                        onDateTrigger={canUseDateCommands ? handleDateTrigger : undefined}
                    />
                    {!canMentionMessages && !canMentionTodos && !canMentionUsers ? (
                        <UpgradeMessage 
                            feature="todoToMessage" 
                            currentPlan={currentPlan}
                            className="mt-2"
                        />
                    ) : (
                        <>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                💡 @ pour mentionner {[
                                    canMentionMessages && 'des messages',
                                    canMentionTodos && 'des todos',
                                    canMentionUsers && 'des utilisateurs'
                                ].filter(Boolean).join(' et ')}{canUseDateCommands ? ' · // pour insérer une date dynamique (ex : //Mois-1, //Année+1)' : ''}
                            </p>
                            {canMentionMessages && canMentionTodos && !canMentionUsers && (
                                <UpgradeMessage 
                                    feature="userMentions" 
                                    currentPlan={currentPlan}
                                    className="mt-2"
                                />
                            )}
                        </>
                    )}
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Type
                    </label>
                    <div className="flex items-center gap-3">
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-black dark:focus:border-white focus:ring focus:ring-black dark:focus:ring-white px-2 py-1"
                        >
                            {normalizedTypes.map(t => (
                                <option key={t.name} value={t.name}>{t.name}</option>
                            ))}
                        </select>
                        <span
                            aria-hidden
                            style={{
                                width: 30,
                                height: 30,
                                marginTop: 4,
                                borderRadius: 4,
                                backgroundColor: (normalizedTypes.find(tt => tt.name === type)?.color) || '#000',
                                flexShrink: 0
                            }}
                            title={type}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Rôle
                        </label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-black dark:focus:border-white focus:ring focus:ring-black dark:focus:ring-white px-2 py-1"
                        >
                            {roles.map(r => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Priorité
                        </label>
                        <select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-black dark:focus:border-white focus:ring focus:ring-black dark:focus:ring-white px-2 py-1"
                        >
                            <option value="critique">»» Critique (urgente)</option>
                            <option value="haute">» Haute</option>
                            <option value="moyenne">› Moyenne</option>
                            <option value="basse">= Basse</option>
                            <option value="minimale">− Minimale</option>
                        </select>
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        type="submit"
                        data-tutorial="message-save-btn"
                        className="flex-1 bg-primary dark:text-black text-white font-semibold py-2 px-4 rounded hover:bg-primary-hover transition"
                        disabled={loading}
                    >
                        {loading
                            ? ((message && message._id) ? 'Mise à jour ...' : 'Création ...')
                            : ((message && message._id) ? 'Mettre à jour' : 'Créer')}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold py-2 px-4 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
                    >
                        Annuler
                    </button>
                </div>
            </form>

            <SuggestionDropdown
                open={suggestOpen}
                items={suggestItems}
                onSelect={applySuggestion}
                anchorRect={suggestAnchor}
                activeIndex={suggestActive}
                onActiveIndexChange={setSuggestActive}
            />
            {canUseDateCommands && (
                <DateDropdown
                    open={dateSuggestOpen}
                    items={dateSuggestItems}
                    onSelect={applyDateSuggestion}
                    anchorRect={dateSuggestAnchor}
                    activeIndex={dateSuggestActive}
                    onActiveIndexChange={setDateSuggestActive}
                />
            )}
        </div>
    );
};

export default MessageEditor;